<template>
  <div class="cash-in-out" data-cy="cash-transfers-modal">
    <SDialog :open="dialogFormVisible" @update:open="onOpenChange">
      <SDialogContent
        :show-close-button="false"
        class="cash-in-out !w-[25%] !max-w-[25%]"
        data-cy="cash-transfers-dialog"
        @open-auto-focus.prevent
        @pointer-down-outside.prevent
        @focus-outside.prevent
      >
        <SDialogHeader>
          <SDialogTitle>Cash In/Out</SDialogTitle>
        </SDialogHeader>
        <div
          style="
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          "
        >
          <div class="flex flex-row gap-2">
            <SButton
              type="button"
              class="flex-1"
              :variant="isCashIn ? 'default' : 'outline'"
              @click="cashInType"
              data-cy="cash-in-button"
            >
              {{ t("cashfloat.cash-in") }}
            </SButton>
            <SButton
              type="button"
              class="flex-1"
              :variant="isCashOut ? 'default' : 'outline'"
              @click="cashOutType"
              data-cy="cash-out-button"
            >
              {{ t("cashfloat.cash-out") }}
            </SButton>
          </div>

          <div style="margin-top: 20px; margin-bottom: 20px">
            <p>
              <strong>{{ t("cashfloat.cash-value") }}</strong>
            </p>
            <div class="mt-1">
              <SInputGroup>
                <SInputGroupInput
                  class="font-bold"
                  :model-value="amount"
                  @update:model-value="(v) => (amount = Number(v))"
                  type="number"
                  @focus="handleInputFocus"
                  @blur="handleInputBlur"
                  data-cy="amount-input"
                />
                <SInputGroupAddon align="inline-end" class="has-[>button]:mr-0">
                  <SSelect v-model="currency">
                    <SSelectTrigger
                      class="w-[115px] rounded-none border-0 bg-transparent font-bold shadow-none focus:ring-0 focus-visible:ring-0"
                    >
                      <SSelectValue placeholder="Select" />
                    </SSelectTrigger>
                    <SSelectContent>
                      <SSelectItem
                        v-for="item in groupCurrencies"
                        :key="item.id"
                        :value="item.label"
                      >
                        {{ item.label }}
                      </SSelectItem>
                    </SSelectContent>
                  </SSelect>
                </SInputGroupAddon>
              </SInputGroup>
            </div>
          </div>

          <div>
            <p v-if="isCashOut">
              <strong>{{ t("cashfloat.to") }}</strong>
            </p>
            <p v-if="isCashIn">
              <strong>{{ t("cashfloat.from") }}</strong>
            </p>
            <SSelect v-model="locationId">
              <SSelectTrigger class="mt-1 w-full" data-cy="location-select">
                <SSelectValue :placeholder="$t('location')" />
              </SSelectTrigger>
              <SSelectContent>
                <SSelectItem
                  v-for="item in locationsList"
                  :key="Number(item.id)"
                  :value="Number(item.id)"
                  data-cy="location-option"
                >
                  {{ String(item.name) }}
                </SSelectItem>
              </SSelectContent>
            </SSelect>
          </div>
        </div>

        <!-- Success state — replaces form content after transfer completes -->
        <div
          v-if="isSuccess"
          class="flex flex-col items-center gap-5 py-8 px-4"
          data-cy="cash-transfer-success"
        >
          <div
            class="flex items-center justify-center w-16 h-16 rounded-full bg-green-50 border-2 border-green-200"
          >
            <svg
              class="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <div class="text-center">
            <p class="text-lg font-semibold">
              {{ t("cashtransfer.cash-in-out-success") }}
            </p>
            <p class="text-sm text-muted-foreground mt-1">
              {{ successDescription }}
            </p>
          </div>
          <SButton
            type="button"
            variant="outline"
            class="w-full mt-2"
            @click="closeModal"
            data-cy="cash-transfer-success-close"
          >
            {{ $t("button.close") }}
          </SButton>
        </div>

        <template v-if="!isSuccess">
          <SDialogFooter class="gap-3">
            <SButton
              type="button"
              variant="outline"
              class="min-w-[120px]"
              @click="closeModal"
              data-cy="cancel-button"
            >
              {{ $t("button.cancel") }}
            </SButton>
            <SButton
              type="button"
              class="min-w-[120px]"
              :loading="isLoading"
              :disabled="!canConfirm || isLoading"
              @click="submit"
              data-cy="submit-button"
            >
              {{ t("cashfloat.submit") }}
            </SButton>
          </SDialogFooter>
        </template>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { ModalComponents, ModalStatus } from "@/constants/modal";
import ModalModule from "@/store/modules/modal";
import { useI18n } from "vue-i18n";
import DeviceModule from "@/store/modules/device";
import { ICashTransfer } from "@/interfaces/cash-float/cash-float";
import dayjs from "dayjs";
import POSConfigModule from "@/store/modules/pos-config";
import UserModule from "@/store/modules/user";
import CashFloatModule from "@/store/modules/cash-float";
import randomstring from "randomstring";
import { signCashTransferTransaction } from "@/utils/helpers/fiskaly";
import { handleCashTransferSync } from "@/utils/vue-helpers/cash-float";
import NotificationService from "@/services/notification.service";
import { MoneyInOutType } from "@smeetz/compliance-common";
import { PaymentMethod } from "@smeetz/order-common";
import {
  mapCashLocation,
  processMoneyInOutEvent,
} from "@/utils/helpers/compliance";
import { LogService } from "@/services/logging";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupInput,
  SSelect,
  SSelectTrigger,
  SSelectValue,
  SSelectContent,
  SSelectItem,
} from "@smeetz/ui-core";

export default defineComponent({
  name: ModalComponents.CashInOut,
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
    SInputGroup,
    SInputGroupAddon,
    SInputGroupInput,
    SSelect,
    SSelectTrigger,
    SSelectValue,
    SSelectContent,
    SSelectItem,
  },
  setup() {
    const { t } = useI18n({});
    const dialogFormVisible = ref(true);
    const modalVisible = dialogVisibleForStatus(ModalStatus.CashInOut);
    const isLoading = ref<boolean>(false);
    const isSuccess = ref<boolean>(false);
    const successDescription = ref<string>("");
    const isCashIn = ref<boolean>(true);
    const isCashOut = ref<boolean>(false);
    const amount = ref<number>(0.0);
    const locationId = ref<string>("");
    // const currencies = [
    //   { id: "CHF", label: "CHF" },
    //   { id: "EUR", label: "EUR" },
    // ];

    const groupCurrencies = computed(() => {
      // enable group currency only for cash in/out for now
      const groupCurrency = POSConfigModule.groupCurrencies.find(
        (currency) => currency.isDefault === 1
      );
      if (groupCurrency) {
        return [groupCurrency];
      }
      return [POSConfigModule.groupCurrencies[0]];
    });

    const groupCurrency = computed(() => {
      const currency =
        groupCurrencies.value.find((c) => c.isDefault === 1) ||
        groupCurrencies.value[0];
      return currency;
    });
    const currency = ref<string>(groupCurrency.value.label);

    const locationsList = computed(() => {
      return DeviceModule.locations.filter(
        (l) => l.id !== Number(hardwareLocationId.value)
      );
    });

    const hardwareLocationId = computed(() => {
      return DeviceModule.hardwareLocation
        ? DeviceModule.hardwareLocation.locationId
        : null;
    });

    const canConfirm = computed(() => {
      // user can confirm if
      // he entered amount greater than 0
      // he has at least one of "from" OR "to"
      const from = isCashOut.value
        ? hardwareLocationId.value
        : locationId.value;
      const to = isCashIn.value ? hardwareLocationId.value : locationId.value;
      return amount.value && from && to;
    });

    function cashInType() {
      isCashIn.value = true;
      isCashOut.value = false;
    }

    function cashOutType() {
      isCashIn.value = false;
      isCashOut.value = true;
    }

    function handleInputFocus(event: any) {
      if (event.target.value === "0") {
        event.target.value = "";
      }
    }

    function handleInputBlur(event: any) {
      if (event.target.value === "") {
        event.target.value = "0";
      }
    }
    return {
      t,
      modalVisible,
      dialogFormVisible,
      cashInType,
      cashOutType,
      isCashIn,
      isCashOut,
      amount,
      currency,
      groupCurrencies,
      locationsList,
      locationId,
      isLoading,
      isSuccess,
      successDescription,
      canConfirm,
      hardwareLocationId,
      handleInputFocus,
      handleInputBlur,
    };
  },
  methods: {
    onOpenChange(open: boolean) {
      if (!open) {
        ModalModule.setStatus(ModalStatus.Hidden);
      }
    },
    closeModal() {
      ModalModule.setStatus(ModalStatus.Hidden);
    },
    async submit() {
      if (this.isLoading) return;
      this.isLoading = true;
      const groupId = POSConfigModule.groupId || UserModule.getUserGroupId;
      // Add a new CashTransfer of type (IN)
      const cashTransferIn: ICashTransfer = {
        _id: dayjs().toISOString(),
        createdAt: dayjs().toISOString(),
        amount: Number(this.amount),
        currency: this.currency,
        groupId: Number(groupId),
        from: this.isCashOut ? this.hardwareLocationId : this.locationId,
        to: this.isCashIn ? this.hardwareLocationId : this.locationId,
        note: "",
        channel: "Smeetz POS",
        uuid: randomstring.generate(10),
      };

      const isSynced = await handleCashTransferSync(cashTransferIn);
      if (!isSynced) {
        this.isLoading = false;
        return;
      }

      // process money in/out event after cash transfer
      try {
        const eventData = {
          moneyInOutType: this.isCashOut
            ? MoneyInOutType.PAY_OUT
            : MoneyInOutType.PAY_IN,
          amount: this.isCashOut ? -Number(this.amount) : Number(this.amount),
          description: `Cash transfer ${this.isCashOut ? "OUT" : "IN"}`,
          posDateTime: dayjs().toISOString(),
          paymentType: PaymentMethod.CASH,
          cashLocation: mapCashLocation(Number(this.locationId)),
        };
        await processMoneyInOutEvent(eventData);
      } catch (error) {
        LogService.info("Failed process money in/out event", { error });
      }

      // display a success message
      NotificationService.showCustomSuccess(
        this.t("cashtransfer.cash-in-out-success")
      );

      // lets update transferAmount in the state
      const transferAmount = this.isCashOut
        ? -1 * Number(this.amount)
        : Number(this.amount);
      CashFloatModule.updateTransfersAmount(transferAmount * 100);
      // lets update sum of cash in and cash out
      if (this.isCashIn) {
        CashFloatModule.updateSumOfCashIn(Number(this.amount) * 100);
      }
      if (this.isCashOut) {
        CashFloatModule.updateSumOfCashOut(Number(this.amount) * 100);
      }

      const amountInCents = this.isCashOut ? -1 * this.amount : this.amount;
      await signCashTransferTransaction(cashTransferIn.uuid, amountInCents);
      this.isLoading = false;

      // Show explicit success screen — cashier confirms they saw the transfer
      // went through before closing, preventing resubmission (L3-7594).
      const selectedLocation = this.locationsList.find(
        (l) => Number(l.id) === Number(this.locationId)
      );
      // The arrow follows the money: a cash in comes FROM the location into
      // the drawer, a cash out goes TO it.
      this.successDescription = this.isCashIn
        ? `${this.amount} ${this.currency} ← ${selectedLocation?.name ?? ""}`
        : `${this.amount} ${this.currency} → ${selectedLocation?.name ?? ""}`;
      this.isSuccess = true;

      // if we're in Sales Closure page, refresh the cash float details
      if (CashFloatModule.isClosingCashfloat) {
        CashFloatModule.setIsClosingCashfloat(false);
        await this.$nextTick();
        CashFloatModule.setIsClosingCashfloat(true);
      }
    },
  },
});
</script>
